import { fetchJson } from "../lib/api";

export type OrderConfirmationReviewStatus =
  | "open"
  | "query_required"
  | "approved"
  | "rejected";

export type OrderConfirmationReviewAction =
  | "approve"
  | "query"
  | "reject"
  | "reopen";

export type OrderConfirmationLineStatus =
  | "ok"
  | "over_reference"
  | "no_reference"
  | "unmatched"
  | "no_price";

export type OrderConfirmationReferenceEvidence = {
  invoice_doc_id: string | null;
  doc_number: string | null;
  doc_date: string | null;
  line_pos: string | null;
  page_no: string | number | null;
  qty: string | number | null;
  uom: string | null;
  net_unit_price: string | number | null;
  line_net_amount: string | number | null;
  source_kind: string | null;
  wholesale_file_id: string | null;
  source_file_id: string | null;
  source_sha256: string | null;
};

export type OrderConfirmationRow = {
  order_confirmation_id: string;
  doc_number: string | null;
  doc_date: string | null;
  supplier: string | null;
  currency: string | null;
  page_count: string | number | null;
  line_count: string | number | null;
  priced_line_count: string | number | null;
  positions_total: string | number | null;
  pdf_available: boolean;
  source_file_id: string | null;
  source_sha256: string | null;
  review_status: OrderConfirmationReviewStatus;
  review_comment: string | null;
  reviewed_at: string | null;
  reviewed_by_user_id: string | null;
  reviewed_by_label: string | null;
  warning_acknowledged: boolean;
};

export type OrderConfirmationLine = {
  line_pos: string;
  page_no: string | number | null;
  article_code: string | null;
  alt_article_code: string | null;
  manufacturer_article_code: string | null;
  description: string | null;
  qty: string | number | null;
  uom: string | null;
  net_unit_price: string | number | null;
  line_net_amount: string | number | null;
  article_group_key: string | null;
  resolution_source: string | null;
  resolution_confidence: string | null;
  resolution_evidence: string | null;
  article_detail_available: boolean;
  project_key: string | null;
  delivery_address_display: string | null;
  match_status: "matched" | "unmatched";
  reference_kind: "12m" | "24m" | "alltime" | null;
  reference_price: string | number | null;
  reference_count: number;
  reference_first_date: string | null;
  reference_last_date: string | null;
  reference_calculation:
    | "quantity_weighted_mean"
    | "arithmetic_mean"
    | null;
  reference_evidence: OrderConfirmationReferenceEvidence[];
  delta_abs: string | number | null;
  delta_pct: string | number | null;
  line_delta: string | number | null;
  best_price: string | number | null;
  best_price_saving: string | number | null;
  status: OrderConfirmationLineStatus;
  has_warning: boolean;
};

export type OrderConfirmationSummary = {
  ok_count: number;
  over_reference_count: number;
  no_reference_count: number;
  unmatched_count: number;
  no_price_count: number;
  warning_count: number;
  /** Mehrkosten: Menge × Abweichung über der Referenz. */
  positive_difference_total: string | number;
  /** Ersparnis: Menge × Abweichung unter der Referenz, als positiver Betrag. */
  saving_total: string | number;
  /** Summe der Abweichungen: Mehrkosten minus Ersparnis. */
  net_delta_total: string | number;
  /** Möglich zum Bestpreis: Menge × Abstand zum niedrigsten Vergleichspreis. */
  best_price_saving_total: string | number;
};

export type OrderConfirmationReviewEvent = {
  event_id: string;
  order_doc_id: string;
  action: OrderConfirmationReviewAction;
  previous_status: OrderConfirmationReviewStatus;
  status: OrderConfirmationReviewStatus;
  comment: string | null;
  warning_acknowledged: boolean;
  actor_user_id: string | null;
  actor_label: string | null;
  created_at: string | null;
  review_snapshot: Record<string, unknown>;
  position_snapshot: OrderConfirmationPositionSnapshot[];
};

export type OrderConfirmationPositionSnapshot = {
  line_pos: string | null;
  page_no: string | number | null;
  article_code: string | null;
  manufacturer_article_code: string | null;
  description: string | null;
  qty: string | number | null;
  uom: string | null;
  net_unit_price: string | number | null;
  line_net_amount: string | number | null;
  article_group_key: string | null;
  resolution_source: string | null;
  resolution_confidence: string | null;
  resolution_evidence: string | null;
  match_status: "matched" | "unmatched";
  reference_kind: "12m" | "24m" | "alltime" | null;
  reference_price: string | number | null;
  reference_count: number;
  reference_first_date: string | null;
  reference_last_date: string | null;
  reference_calculation?:
    | "quantity_weighted_mean"
    | "arithmetic_mean"
    | null;
  reference_evidence?: OrderConfirmationReferenceEvidence[];
  delta_abs: string | number | null;
  delta_pct: string | number | null;
  line_delta: string | number | null;
  status: OrderConfirmationLineStatus;
};

export type OrderConfirmationListResponse = {
  total: number;
  status_counts: Record<OrderConfirmationReviewStatus, number>;
  limit: number;
  offset: number;
  status: OrderConfirmationReviewStatus | null;
  items: OrderConfirmationRow[];
};

export type OrderConfirmationDetailResponse = {
  assessment_token: string;
  order_confirmation: OrderConfirmationRow;
  line_total: number;
  lines: OrderConfirmationLine[];
  summary: OrderConfirmationSummary;
  review_events: OrderConfirmationReviewEvent[];
  evidence_boundary: {
    source_documents_changed: false;
    supplier_portal_action: false;
    message: string;
  };
};

export async function fetchOrderConfirmations(params: {
  q?: string;
  status?: OrderConfirmationReviewStatus;
  limit?: number;
  offset?: number;
}): Promise<OrderConfirmationListResponse> {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return fetchJson<OrderConfirmationListResponse>(
    `/api/order-confirmations${query ? `?${query}` : ""}`,
  );
}

export async function fetchOrderConfirmationDetail(
  orderConfirmationId: string,
): Promise<OrderConfirmationDetailResponse> {
  return fetchJson<OrderConfirmationDetailResponse>(
    `/api/order-confirmations/${encodeURIComponent(orderConfirmationId)}`,
  );
}

export async function recordOrderConfirmationDecision(args: {
  orderConfirmationId: string;
  action: OrderConfirmationReviewAction;
  comment?: string | null;
  warningAcknowledged?: boolean;
  assessmentToken: string;
}): Promise<{
  event: OrderConfirmationReviewEvent;
  detail: OrderConfirmationDetailResponse;
}> {
  return fetchJson(
    `/api/order-confirmations/${encodeURIComponent(args.orderConfirmationId)}/decisions`,
    {
      method: "POST",
      body: JSON.stringify({
        action: args.action,
        comment: args.comment?.trim() ? args.comment.trim() : null,
        warning_acknowledged: args.warningAcknowledged ?? false,
        assessment_token: args.assessmentToken,
      }),
    },
  );
}
